import { useEffect, useContext, useState } from "react";
import { UserContext } from "../../Context";
import { useProductMaster } from "../Inventory/Hooks/useProductMaster";
import { useWarehouse } from "../Inventory/Hooks/useWarehouse";
import { STOCK_MODAL_MODES } from "../Inventory/constants";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { ArrowLeft, Trash2 } from "lucide-react";
import { EDIT, SAN_LIST, VIEW } from "../../routes";
import {
  GenericTable,
  SearchableDropdown,
  ProductCategoryFilter,
  RoleValidator,
  Spinner,
} from "../../Components/Common";
import {
  getSANStatusColor,
  MATERIAL_COLUMNS_SAN,
  MATERIAL_LABELS_SAN,
  SAN_STATUS,
  ROLES,
} from "./constants";
import { toProperCase } from "../../Utils";
import { useSAN } from "./Hooks/useSAN.js";

const StockAdjustmentNoteActions = () => {
  const { fetchProductById, fetchProducts, getAllMakes, fetchCategories } =
    useProductMaster();
  const [loading, setLoading] = useState(true);
  const { newSAN, fetchSANById, updateSANDetails } = useSAN();
  const { fetchWarehouses } = useWarehouse();
  const [mode, setMode] = useState(STOCK_MODAL_MODES.ADD);
  const [warehouse, setWarehouse] = useState("");
  const [status, setStatus] = useState("");
  const [warehouseId, setWarehouseId] = useState("");
  const [productId, setProductId] = useState("");
  const [loadingQuantities, setLoadingQuantities] = useState({});
  const [formData, setFormData] = useState({
    notes: "",
  });
  const location = useLocation();
  const [materials, setMaterials] = useState([]);
  const navigate = useNavigate();
  const param = useParams();
  const [category, setCategory] = useState(null);
  const [make, setMake] = useState(null);

  const isDisabled = mode !== STOCK_MODAL_MODES.ADD;

  const resetForm = () => {
    setFormData({ notes: "" });
    setWarehouseId("");
    setWarehouse("");
    setMaterials([]);
    setStatus("");
  };

  const { user } = useContext(UserContext);
  const currentUserRole = user?.role;

  const removeMaterial = (product) => {
    setMaterials((prev) => prev.filter((item) => item.id !== product.id));
    toast.success(`${product.product_name} removed form the list`);
  };

  const handleAddMaterial = (material) => {
    if (!material) return;
    setMaterials((prev) => {
      // Check if material with same id or name already exists
      const exists = prev.some(
        (item) =>
          item.id === material.id || item.product_name === material.product_name
      );
      if (exists) {
        toast.error(`${material.product_name} already added`);
        return prev;
      } else {
        toast.success(`${material.product_name} added`);
      }
      // skip adding duplicate
      return [...prev, { ...material, product: material.id, quantity: 1 }];
    });
  };

  const renderField = (column, row) => {
    switch (column) {
      case "product_name":
        return (
          <div
            className="flex flex-col"
            disabled={mode === STOCK_MODAL_MODES.VIEW}
          >
            <span className="font-semibold">{row.product_name}</span>
            <span className="text-gray-500 font-normal">
              {row.specification}
            </span>
            <span className="text-gray-800 font-normal italic">
              ({row.UOM_name || row.UOM})
            </span>
          </div>
        );
      case "note":
        return (
          <div className="border-b-2 border-gray-400">
            <input
              type="text"
              value={row.note ?? ""}
              placeholder="Enter note..."
              onChange={(e) =>
                setMaterials((prev) =>
                  prev.map((m) =>
                    m.id === row.id ? { ...m, note: e.target.value } : m
                  )
                )
              }
              disabled={mode === STOCK_MODAL_MODES.VIEW}
              className="w-32 h-7 bg-inherit border-none focus:ring-0  text-start"
            />
          </div>
        );
      case "quantity":
        return (
          <div className="border-b-2 border-gray-400 w-14">
            <input
              type="text"
              value={
                row.quantity === null || row.quantity === "" ? "" : row.quantity
              }
              placeholder="0"
              onChange={(e) => {
                const val = e.target.value;
                if (/^-?\d*(\.\d*)?$/.test(val)) {
                  setMaterials((prev) =>
                    prev.map((m) =>
                      m.id === row.id ? { ...m, quantity: val } : m
                    )
                  );
                }
              }}
              disabled={mode === STOCK_MODAL_MODES.VIEW}
              className="w-18 h-7 bg-inherit border-none focus:ring-0 text-start"
            />
          </div>
        );
      case "available_quantity":
        if (loadingQuantities[row.id]) {
          return <span className="text-gray-400 italic">Loading...</span>;
        }
        return (
          <span className="font-medium text-gray-800">
            {row.available_quantity ?? 0}
          </span>
        );

      case "actions":
        return (
          <>
            <button
              className=" hover:underline ml-2"
              onClick={() => removeMaterial(row)}
            >
              <Trash2 className="h-5 w-5 text-red-400" />
            </button>
          </>
        );
      default:
        return row[column] || "N/A";
    }
  };

  const handleSubmit = async (submitType) => {
    const missingFields = [];
    if (!warehouseId) missingFields.push("Receiving warehouse");
    if (materials.length === 0) missingFields.push("at least one material");

    if (missingFields.length > 0) {
      toast.error(`Please provide: ${missingFields.join(", ")}`);
      return;
    }

    const payload = {
      receiving_warehouse: warehouseId,
      status: submitType,
      notes: formData.notes,
      materials,
    };
    var data = null;
    if (mode === STOCK_MODAL_MODES.ADD) {
      data = await newSAN(payload);
    } else {
      data = await updateSANDetails(param.instanceId, payload);
      setMaterials(data.materials);
      setFormData({
        notes: data.notes,
      });
      setWarehouse(data.receiving_warehouse_name);
      setWarehouseId(data.receiving_warehouse);
      setStatus(data.status);
    }
    navigate(SAN_LIST.replace(":instanceId", data.id));
  };

  const getStockAdjustmentNoteData = async () => {
    setLoading(true);
    try {
      const data = await fetchSANById(param.instanceId);

      if (data) {
        setFormData({
          notes: data.notes || "",
        });
        setWarehouseId(data.receiving_warehouse || "");
        setWarehouse(data.receiving_warehouse_name || "");
        setMaterials(data.materials || []);
        setStatus(data.status || "");
      } else {
        resetForm();
      }
    } catch (error) {
      resetForm();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (param.instanceId) {
      getStockAdjustmentNoteData();
      if (location.pathname.includes(EDIT)) {
        setMode(STOCK_MODAL_MODES.EDIT);
      } else if (location.pathname.includes(VIEW)) {
        setMode(STOCK_MODAL_MODES.VIEW);
      }
    } else {
      setMode(STOCK_MODAL_MODES.ADD);
      setLoading(false);
    }
  }, [param.instanceId, location.pathname]);

  useEffect(() => {
    const fetchProductStock = async () => {
      if (warehouseId && productId) {
        setLoadingQuantities((prev) => ({ ...prev, [productId]: true }));
        const data = await fetchProductById(productId, warehouseId);
        if (data?.available_quantity !== undefined) {
          // Update that specific product in material list
          setMaterials((prev) =>
            prev.map((m) =>
              m.id === productId
                ? { ...m, available_quantity: data.available_quantity }
                : m
            )
          );
        }
        setLoadingQuantities((prev) => {
          const updated = { ...prev };
          delete updated[productId];
          return updated;
        });
      }
    };
    fetchProductStock();
  }, [productId, warehouseId]);

  if (loading)
    return (
      <div className="flex items-center justify-center h-[60vh] pt-10">
        <Spinner size={4} />
      </div>
    );

  return (
    <div className="min-h-[70vh]">
      <div className="flex justify-between items-center mt-2">
        <h2 className="flex gap-2 justify-center items-center text-xl font-semibold text-gray-800 mt-2 px-3 py-2">
          <button
            type="button"
            onClick={() => navigate(SAN_LIST)}
            className="text-primary text-center text-sm font-lg"
          >
            <ArrowLeft size={23} absoluteStrokeWidth className="text-primary" />
          </button>
          {mode === STOCK_MODAL_MODES.EDIT
            ? "Edit"
            : mode === STOCK_MODAL_MODES.VIEW
            ? ""
            : "Create"}{" "}
          Stock Adjustment Note
        </h2>
      </div>
      <div className="mx-auto grid grid-cols-1 md:grid-cols-2 md:gap-6 gap-2 px-4 py-2">
        {/* Left Side — Warehouse & Notes */}
        <div className="bg-white md:border-r-2 md:pr-8">
          <h3 className="text-lg font-semibold text-primary mb-2 md:mb-4 flex items-center gap-2">
            <span className="inline-block w-1.5 h-6 bg-primary rounded-full"></span>
            Details
          </h3>
          <div className="mb-3">
            <label className="block text-gray-700 font-medium md:text-sm text-xs mb-1">
              {`Receiving Warehouse`} <sub className="text-red-500">*</sub>
            </label>
            <SearchableDropdown
              fetchFunction={fetchWarehouses}
              onSelect={(val) => setWarehouseId(val.id)}
              labelKey="name"
              placeholder={warehouse ? warehouse : "Select Warehouse"}
              disabled={isDisabled}
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 font-medium md:text-sm text-xs my-2">
              Notes
            </label>
            <textarea
              rows="2"
              value={formData.notes}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  notes: e.target.value,
                }))
              }
              placeholder="Write any notes about this return..."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-1 focus:ring-blue-500 focus:outline-none resize-none"
              disabled={mode === STOCK_MODAL_MODES.VIEW}
            ></textarea>
          </div>

          {mode !== STOCK_MODAL_MODES.ADD && status && (
            <div>
              <label className="block text-gray-700  font-medium md:text-sm text-xs mb-1">
                Status
              </label>
              <span
                className={`py-1.5 px-3 text-sm md:font-medium rounded-lg ${getSANStatusColor(
                  status
                )}`}
              >
                {toProperCase(status)}
              </span>
            </div>
          )}
        </div>

        {/* Right Side — Selected Materials */}
        <div>
          <h3 className="text-lg font-semibold text-primary mb-2 md:mb-4 flex items-center text-center gap-2">
            <span className="inline-block w-1.5 h-6 bg-primary rounded-full"></span>
            Selected Materials
          </h3>
          {!(mode === STOCK_MODAL_MODES.VIEW) && (
            <>
              <label className="block text-gray-700 font-medium text-xs md:text-sm mb-1">
                Add Product <sub className="text-red-500">*</sub>
              </label>
              <ProductCategoryFilter onProductSelect={handleAddMaterial} />
              <label className="block text-gray-700 font-medium text-xs md:text-sm my-2">
                Selected Product List
              </label>
            </>
          )}

          {materials.length === 0 ? (
            <div className="text-gray-600 text-center py-11 border rounded-lg md:h-[38vh]">
              No materials selected yet.
            </div>
          ) : (
            <div
              className={`overflow-x-auto ${
                mode === STOCK_MODAL_MODES.VIEW
                  ? "h-[40vh] md:h-[50vh]"
                  : mode === STOCK_MODAL_MODES.EDIT
                  ? "h-[27vh] md:h-[38vh]"
                  : "h-[32vh] md:h-[38vh]"
              } overflow-y-auto`}
            >
              {loading ? (
                <div className="h-full w-full flex justify-center items-center">
                  <div className=" animate-spin border-t border-blue-600"></div>
                </div>
              ) : (
                <GenericTable
                  tableColumns={
                    mode === STOCK_MODAL_MODES.VIEW
                      ? MATERIAL_COLUMNS_SAN.filter(
                          (col) =>
                            col !== "actions" && col !== "available_quantity"
                        )
                      : MATERIAL_COLUMNS_SAN
                  }
                  tableRows={materials}
                  columnLabels={MATERIAL_LABELS_SAN}
                  renderField={renderField}
                  enableMobileCardView={false}
                />
              )}
            </div>
          )}

          {/* Action Buttons Section */}
          <div className="flex justify-end gap-3 mt-4">
            {(currentUserRole === ROLES.SITE_ENGINEER ||
              currentUserRole === ROLES.INVENTORY_INCHARGE) &&
              (mode === STOCK_MODAL_MODES.ADD ||
                mode === STOCK_MODAL_MODES.EDIT) && (
                <>
                  <button
                    onClick={() => handleSubmit(SAN_STATUS.DRAFT)}
                    className="px-4 py-2 bg-gray-200 rounded-md text-gray-800 hover:bg-gray-300"
                  >
                    Save as Draft
                  </button>
                  <button
                    onClick={() =>
                      handleSubmit(SAN_STATUS.PENDING_FOR_APPROVAL)
                    }
                    className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark"
                  >
                    Send for Approval
                  </button>
                </>
              )}
            {(currentUserRole === ROLES.PROJECT_MANAGER ||
              currentUserRole === ROLES.INVENTORY_ADMIN) &&
              mode === STOCK_MODAL_MODES.EDIT && (
                <>
                  <button
                    onClick={() => handleSubmit(SAN_STATUS.APPROVED)}
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                  >
                    Approve
                  </button>
                  <button
                    onClick={() => handleSubmit(SAN_STATUS.REJECTED)}
                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                  >
                    Reject
                  </button>
                </>
              )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StockAdjustmentNoteActions;
